import { Transition } from '@headlessui/react'
import yaml, { YAMLException } from 'js-yaml'
import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { JSONSchema3or4 } from 'to-json-schema'

import Button from '@/components/TailwindUI/Button'
import Monaco, { OnChange } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Monaco'
import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import useSearchParams from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useSearchParams'
import dump from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/dumpYaml'
import convertJSONtoSchema from '@/pages/SpaceAppBuilder/utilities/convertJSONToSchema'

export function Model() {
  const [datasource, setDatasource] = useDatasource(true)

  const datasourceEventActions: typeof datasource.events = datasource.events

  const [searchParams] = useSearchParams()

  const activeEventActionKey = searchParams.get('eventAction')!

  const [resultOverrideValue, setResultOverrideValue] = useState<null | object>(null)
  const [invalidJson, setInvalidJson] = useState(false)
  const [yamlSchema, setYamlSchema] = useState<JSONSchema3or4>()

  const updateEventModelMutation = useMutation({
    mutationFn: async (variables: { eventActionKey: string; results: object }) => {
      const schema = convertJSONtoSchema(variables.results)
      setYamlSchema(schema)

      return setDatasource(datasource => ({
        ...datasource,
        events: {
          ...datasource.events,
          [variables.eventActionKey]: {
            ...datasource.actions[variables.eventActionKey],
            eventModel: schema
          }
        }
      }))
    }
  })

  const handleEventModelChange: OnChange = (value = '') => {
    try {
      if (!activeEventActionKey) return

      setDatasource(datasource => ({
        ...datasource,
        events: {
          ...datasource.events,
          [activeEventActionKey]: {
            ...datasource.actions[activeEventActionKey],
            eventModel: yaml.load(value)
          }
        }
      }))
    } catch (error) {
      if (error instanceof YAMLException) return
      throw error
    }
  }

  const handleGenerateEventModelChange: OnChange = (value = '') => {
    if (!activeEventActionKey) return

    let parsedValue
    try {
      parsedValue = JSON.parse(value)
    } catch {
      setInvalidJson(true)
      return null
    }

    setInvalidJson(false)
    setResultOverrideValue(parsedValue)
  }

  useEffect(() => {
    if (updateEventModelMutation.isSuccess) {
      const timeoutId = setTimeout(() => updateEventModelMutation.reset(), 5000)
      return () => clearTimeout(timeoutId)
    }
  }, [updateEventModelMutation.isSuccess, updateEventModelMutation.reset])

  return (
    <>
      <div className="flex flex-1 flex-col">
        <div className="bg-gray-100 px-4 py-2 text-xs">Event Model</div>
        <Monaco
          key={activeEventActionKey}
          language="yaml"
          defaultValue={dump(datasourceEventActions[activeEventActionKey].eventModel)}
          onChange={handleEventModelChange}
          value={dump(yamlSchema)}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="bg-gray-100 px-4 py-2 text-xs">Generate Event Schema</div>
        <div className="relative flex-1">
          <div className="absolute right-20 top-2.5 z-10 flex flex-col items-center">
            <div className="flex">
              <Transition
                show={invalidJson}
                enter="transition-opacity duration-125"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as="div"
                className="pointer-events-none pr-2 text-sm font-thin text-gray-400"
              >
                Invalid JSON
              </Transition>
              <Transition
                show={updateEventModelMutation.isSuccess || updateEventModelMutation.isError}
                enter="transition-opacity duration-125"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                as="div"
                className="pointer-events-none pr-2 text-sm font-thin text-gray-400"
              >
                {updateEventModelMutation.isSuccess
                  ? 'Success!'
                  : updateEventModelMutation.isError
                    ? 'Error updating schema'
                    : ''}
              </Transition>
              <Button
                color="secondary"
                disabled={invalidJson}
                onClick={() => {
                  if (activeEventActionKey && resultOverrideValue) {
                    updateEventModelMutation.mutate({
                      eventActionKey: activeEventActionKey,
                      results: resultOverrideValue
                    })
                  }
                }}
              >
                Generate Event Schema
              </Button>
            </div>
          </div>
          <Monaco key={activeEventActionKey} language="json" onChange={handleGenerateEventModelChange} />
        </div>
      </div>
    </>
  )
}
